import React, { useEffect, useState, useRef } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";
import { formatRp } from "@/lib/tiers";
import { Trophy, RefreshCw } from "lucide-react";

function Board({ scope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = scope === "monthly" ? "/leaderboard/monthly" : "/leaderboard/all-time";
      const { data } = await api.get(url, { params: { limit: 50 } });
      setRows(data.entries || []);
      setUpdatedAt(new Date());
      setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 600000); // every 10 min
    return () => { if (timer.current) clearInterval(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  if (disabled) return <div className="text-center py-10 text-zinc-500 font-mystic">Leaderboards are currently disabled.</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 text-xs text-zinc-500">
        <span>{loading ? "Loading…" : `Top ${rows.length} contributors`}</span>
        <button onClick={load} className="inline-flex items-center gap-1 hover:text-rose-300" data-testid={`refresh-${scope}`}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
          {updatedAt && <span className="ml-2 text-zinc-600">(auto every 10 min)</span>}
        </button>
      </div>
      {rows.length === 0 && !loading ? (
        <div className="text-center text-zinc-500 py-12 font-mystic italic">No approved contributions {scope === "monthly" ? "this month" : "yet"}.</div>
      ) : (
        <ol className="space-y-2" data-testid={`leaderboard-list-${scope}`}>
          {rows.map((r) => (
            <li key={r.user_id} className={`glass rounded-xl p-4 flex items-center gap-4 ${r.rank <= 3 ? "border-amber-500/30" : ""}`} data-testid={`leaderboard-row-${r.user_id}`}>
              <div className={`w-10 text-center font-display text-xl font-black ${
                r.rank === 1 ? "text-amber-300" : r.rank === 2 ? "text-zinc-200" : r.rank === 3 ? "text-orange-300" : "text-zinc-500"
              }`}>#{r.rank}</div>
              <Avatar user={r} size={48} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-white truncate">{r.name}</div>
                <div className="mt-1"><RoleBadge role={r.role} badges={r.badges} size="xs" /></div>
              </div>
              <div className="text-right">
                <div className="font-display font-bold text-white">{formatRp(r.total_contribution)}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Contribution</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function LeaderboardPage() {
  const { settings } = useSettings();
  if (settings.leaderboard_enabled === false) {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto px-6 py-32 text-center">
          <div className="font-display text-3xl font-bold text-white">Supporter Leaderboard</div>
          <p className="text-zinc-400 mt-3 font-mystic italic">Currently disabled by the order.</p>
        </section>
      </PageShell>
    );
  }
  return (
    <PageShell>
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="font-mystic text-amber-300 text-sm tracking-widest inline-flex items-center gap-2 justify-center">
            <Trophy className="w-4 h-4" /> SUPPORTER LEADERBOARD <Trophy className="w-4 h-4" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white mt-3 tracking-tighter">Hall of Tributes</h1>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto font-mystic italic">
            Ranked by total approved contributions. Refreshes every 10 minutes.
          </p>
        </div>
        <Tabs defaultValue="all_time">
          <TabsList className="bg-zinc-950 border border-white/10">
            <TabsTrigger value="all_time" data-testid="lb-tab-all">All Time</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="lb-tab-monthly">This Month</TabsTrigger>
          </TabsList>
          <TabsContent value="all_time" className="mt-6"><Board scope="all_time" /></TabsContent>
          <TabsContent value="monthly" className="mt-6"><Board scope="monthly" /></TabsContent>
        </Tabs>
      </section>
    </PageShell>
  );
}
